import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/core/styles";
import {
  fetchPendingTerms,
  acceptTerms,
} from "../../redux/organization/actions";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "80vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 800,
    width: "100%",
  },
  termItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
}));

const TermsAccept = ({ history }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const pendingTerms = useSelector((s) => s.pendingTerms);
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchPendingTerms()).then(() => setHasLoaded(true));
  }, [dispatch]);

  useEffect(() => {
    if (hasLoaded && pendingTerms && pendingTerms.length === 0) {
      // All terms accepted, redirect to main page
      if (history) {
        history.push("/");
      }
    }
  }, [pendingTerms, hasLoaded, history]);

  const handleCheckboxChange = (termsVersionId) => {
    setAccepted((prev) => ({
      ...prev,
      [termsVersionId]: !prev[termsVersionId],
    }));
  };

  const allAccepted =
    pendingTerms &&
    pendingTerms.length > 0 &&
    pendingTerms.every((term) => accepted[term.termsVersionId]);

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      for (const term of pendingTerms) {
        if (accepted[term.termsVersionId]) {
          await dispatch(acceptTerms(term.termsVersionId));
        }
      }
      // Refresh pending terms to check if all are accepted
      await dispatch(fetchPendingTerms());
    } catch (error) {
      console.error("Error accepting terms:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingTerms || pendingTerms.length === 0) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h5" gutterBottom align="center">
          Akceptacja regulaminów
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          align="center"
          color="textSecondary"
        >
          Aby kontynuować korzystanie z systemu, musisz zaakceptować poniższe
          regulaminy.
        </Typography>

        <Box mt={3}>
          {pendingTerms.map((term) => (
            <Box key={term.termsVersionId} className={classes.termItem}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>{term.termsTypeName}</strong> - {term.versionName}{" "}
                    (v{term.versionNumber})
                  </Typography>
                  {term.effectiveFrom && (
                    <Typography variant="body2" color="textSecondary">
                      Obowiązuje od: {term.effectiveFrom}
                    </Typography>
                  )}
                </Grid>
                {term.filePath && (
                  <Grid item xs={12}>
                    <a
                      href={`/org-media/files${term.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {term.filename || "Pobierz PDF"}
                    </a>
                  </Grid>
                )}
                {!term.filePath && term.filename && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Plik: {term.filename}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!accepted[term.termsVersionId]}
                        onChange={() =>
                          handleCheckboxChange(term.termsVersionId)
                        }
                        color="primary"
                      />
                    }
                    label="Akceptuję powyższy regulamin"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>

        <Button
          className={classes.submitButton}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={!allAccepted || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Zapisywanie..." : "Potwierdź akceptację"}
        </Button>
      </Paper>
    </div>
  );
};

export default TermsAccept;
